<template>
  <div class="chat-video-player" :style="containerStyle">
    <!-- Loading state -->
    <div v-if="isLoading" class="video-loading">
      <div class="video-spinner"></div>
    </div>
    
    <!-- Error state with download fallback -->
    <div v-else-if="hasError" class="video-error">
      <i class="bi bi-film"></i>
      <span>{{ errorText }}</span>
      <a :href="videoUrl" target="_blank" rel="noopener" class="download-btn">
        <i class="bi bi-download"></i>
        {{ downloadText }}
      </a>
    </div>
    
    <!-- Video player -->
    <video
      v-else-if="blobUrl"
      ref="videoEl"
      controls
      playsinline
      preload="auto"
      :src="blobUrl"
      :style="videoStyle"
      @error="onVideoError"
    >
      {{ fallbackText }}
    </video>
  </div>
</template>

<script>
export default {
  name: 'ChatVideoPlayer',
  props: {
    videoUrl: {
      type: String,
      required: true,
    },
    filename: {
      type: String,
      default: '',
    },
    width: {
      type: [Number, String],
      default: 220,
    },
    height: {
      type: [Number, String],
      default: 180,
    },
  },
  data() {
    return {
      isLoading: true,
      hasError: false,
      blobUrl: null,
    };
  },
  computed: {
    containerStyle() {
      return {
        width: typeof this.width === 'number' ? `${this.width}px` : this.width,
        height: typeof this.height === 'number' ? `${this.height}px` : this.height,
        maxWidth: typeof this.width === 'number' ? `${this.width}px` : this.width,
        maxHeight: typeof this.height === 'number' ? `${this.height}px` : this.height,
      };
    },
    videoStyle() {
      return {
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        background: '#000',
      };
    },
    errorText() {
      return this.$t?.('chat.video_load_error') || 'تعذر تشغيل الفيديو';
    },
    downloadText() {
      return this.$t?.('common.download') || 'تحميل';
    },
    fallbackText() {
      return this.$t?.('chat.video_not_supported') || 'Your browser does not support the video tag.';
    },
    // Detect MIME type from filename or URL
    detectedMimeType() {
      const name = (this.filename || this.videoUrl || '').toLowerCase();
      if (/\.mp4/i.test(name)) return 'video/mp4';
      if (/\.webm/i.test(name)) return 'video/webm';
      if (/\.mov/i.test(name)) return 'video/mp4';
      if (/\.m4v/i.test(name)) return 'video/mp4';
      return 'video/mp4';
    },
  },
  watch: {
    videoUrl: {
      immediate: true,
      handler(url) {
        if (url) this.loadVideo();
      },
    },
  },
  beforeUnmount() {
    this.cleanup();
  },
  methods: {
    loadVideo() {
      this.isLoading = true;
      this.hasError = false;
      this.cleanup();

      const xhr = new XMLHttpRequest();
      xhr.open('GET', this.videoUrl, true);
      xhr.responseType = 'blob';
      
      xhr.onload = () => {
        if (xhr.status === 200) {
          const blob = xhr.response;
          // Create blob with correct MIME type (fixes Firefox)
          const mimeType = this.detectedMimeType;
          const typedBlob = new Blob([blob], { type: mimeType });
          this.blobUrl = URL.createObjectURL(typedBlob);
          this.isLoading = false;
        } else {
          this.onLoadError();
        }
      };
      
      xhr.onerror = () => this.onLoadError();
      xhr.send();
    },
    onLoadError() {
      console.error('[ChatVideoPlayer] Failed to load:', this.videoUrl);
      this.hasError = true;
      this.isLoading = false;
    },
    onVideoError() {
      console.error('[ChatVideoPlayer] Playback error');
      this.hasError = true;
    },
    cleanup() {
      if (this.blobUrl) {
        URL.revokeObjectURL(this.blobUrl);
        this.blobUrl = null;
      }
    },
  },
};
</script>

<style scoped>
.chat-video-player {
  display: block;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
  position: relative;
}

.video-loading,
.video-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  color: white;
  gap: 8px;
}

.video-loading span,
.video-error span {
  font-size: 12px;
  opacity: 0.8;
}

.video-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.video-error i {
  font-size: 32px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 4px;
}

.video-error span {
  font-size: 11px;
  margin-bottom: 8px;
}

.download-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  font-size: 12px;
  text-decoration: none;
  transition: background 0.2s;
}

.download-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  color: white;
}

.download-btn i {
  font-size: 14px;
  color: white;
}

video {
  display: block;
}
</style>
